import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { FiltersAndPaginationPayloadService } from '../../../@core/services/filters-and-pagination-payload.service';
import { QuantityBreakLabelService } from '../../../@core/services/quantity-break-label.service';
import {
  selectExportOrdersBundleError,
  selectExportOrdersBundleLoading,
} from '../../../@store/orders/orders.selectors';
import { QuotesActions } from '../../../@store/quotes/quotes.actions';
import {
  selectArchivedQuotes,
  selectArchivedQuotesError,
  selectArchivedQuotesLoading,
  selectArchivedQuotesTotalCount,
} from '../../../@store/quotes/quotes.selectors';
import { Quote, QuoteTableData } from '../../../@store/quotes/quotes.state.interface';
import { CONFIG } from '../../../config';
import { SafeAny } from '../../../types/safe-any';
import { PaginationComponent } from '../../@shared/pagination/pagination.component';
import { ChangeQuoteStatusDialogComponent } from '../change-quote-status-dialog/change-quote-status-dialog.component';

@Component({
  selector: 'ciwc-archived-quotes',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatDialogModule,
    MatCardModule,
    MatSelectModule,
    PaginationComponent,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
  ],
  templateUrl: './archived-quotes.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArchivedQuotesComponent implements OnInit, AfterViewInit {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly payloadService = inject(FiltersAndPaginationPayloadService);
  private readonly quantityBreakLabelService = inject(QuantityBreakLabelService);
  readonly quotes = this.store.selectSignal(selectArchivedQuotes);
  readonly quotesLength = this.store.selectSignal(selectArchivedQuotesTotalCount);
  readonly quotesLoading = this.store.selectSignal(selectArchivedQuotesLoading);
  readonly quotesError = this.store.selectSignal(selectArchivedQuotesError);
  readonly exportQuotesBundleLoading = this.store.selectSignal(selectExportOrdersBundleLoading);
  readonly exportQuotesBundleError = this.store.selectSignal(selectExportOrdersBundleError);
  readonly CONFIG = CONFIG;

  protected readonly Math = Math;

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<QuoteTableData>;

  selection = new SelectionModel<QuoteTableData>(true, []);
  dataSource: MatTableDataSource<QuoteTableData> = new MatTableDataSource<QuoteTableData>();
  displayedColumns: string[] = [
    'select',
    'quoteId',
    'description',
    'caNumber',
    'quantity',
    'dateCreated',
    'status',
    'client',
    'assignee',
    'dateUpdated',
    'actions',
  ];
  pageSizeOptions = CONFIG.PAGE_SIZE_OPTIONS;
  pageSize = CONFIG.PAGE_SIZE;
  pageIndex = 0;
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;
  currentSort: Sort = { active: 'dateCreated', direction: 'desc' };

  constructor() {
    effect(() => {
      this.initValues();
    });
  }

  ngOnInit() {
    this.fetchData();
    this.setPagination();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private fetchData(): void {
    this.payloadService.updateSortFromMaterial(this.currentSort);

    const payload: SafeAny = {
      archived: true,
      limit: 10,
      offset: 1,
      sort: '-dateCreated',
      q: this.payloadService.currentQuotesSearchPayload,
    };

    if (this.payloadService.currentFilterPayload.status) {
      payload.status = this.payloadService.currentFilterPayload.status;
    }

    if (this.payloadService.currentFilterPayload.assigneeId) {
      payload.assigneeId = this.payloadService.currentFilterPayload.assigneeId;
    }

    if (this.payloadService.currentFilterPayload.clientId) {
      payload.clientId = this.payloadService.currentFilterPayload.clientId;
    }

    this.store.dispatch(
      QuotesActions.loadArchivedQuotes({
        payload,
      }),
    );
  }

  viewQuoteDetails(quote: Quote): void {
    this.router.navigate([`/quotes/${quote.id}/view`]);
  }

  editQuote(quote: Quote): void {
    this.router.navigate([`/quotes/${quote.id}/edit`]);
  }

  unarchiveQuote(quote: Quote): void {
    const dialogRef = this.dialog.open(ChangeQuoteStatusDialogComponent, {
      data: { quoteId: quote.id, archived: quote.archived },
    });

    dialogRef.afterClosed();
  }

  sortData(sort: Sort): void {
    this.currentSort = sort;
    this.payloadService.updateSortFromMaterial(sort);

    this.store.dispatch(
      QuotesActions.loadArchivedQuotes({
        payload: { ...this.payloadService.combinedPayload, archived: true },
      }),
    );
  }

  handlePageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;

    this.payloadService.updatePaginationPayload({
      offset: this.pageIndex + 1,
      limit: this.pageSize,
    });

    this.store.dispatch(
      QuotesActions.loadArchivedQuotes({
        payload: { ...this.payloadService.combinedPayload, archived: true },
      }),
    );
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;

    return numSelected === numRows;
  }

  checkboxLabel(row?: SafeAny): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
  }

  get hasSelection(): boolean {
    return this.selection.selected.length > 0;
  }

  exportQuotesBundle() {
    if (!this.hasSelection || this.exportQuotesBundleLoading()) return;
    const quotesId = this.selection.selected.map((o) => o.id).join(',');
    this.store.dispatch(QuotesActions.exportQuotesBundle({ quotesId }));
  }

  private initValues(): void {
    const archivedQuotesTableData = this.quotes()?.map((quote: Quote) => ({
      quoteId: quote.quoteId,
      caNumber: quote.caNumber?.number,
      description: quote.description,
      quantity: this.quantityBreakLabelService.getQuantityBreakLabel(quote.quantityBreaks),
      dateCreated: quote.dateCreated,
      status: quote.status,
      client: `${quote.client.firstName} ${quote.client.lastName}, ${quote.client.code}, ${quote.client.company}, ${quote.client.address}`,
      assignee: quote.assignee ? quote.assignee?.firstName + ' ' + quote.assignee?.lastName : 'N/A',
      lastEdited: quote.dateUpdated,
      id: quote.id,
      archived: quote.archived,
    }));

    this.dataSource.data = archivedQuotesTableData as QuoteTableData[];
  }

  private setPagination(): void {
    this.payloadService.paginationPayload$.subscribe((value) => {
      if (!value) return;

      this.pageSize = value.limit;
      this.pageIndex = value.offset - 1;
    });
  }
}
